import React, { useState, useEffect } from 'react';

/* ------------------ Small form helpers ------------------ */

export function Input({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="text-sm text-slate-300">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full rounded-md bg-slate-950/70 border border-slate-800 p-2 text-slate-200"
      />
    </div>
  );
}

export function Segmented({ label, value, onChange, options, labels = {} }) {
  return (
    <div>
      <label className="text-sm text-slate-300">{label}</label>
      <div className="mt-2 flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`px-3 py-2 rounded-md text-sm transition ${
              opt === value
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-900/60 text-slate-200 border border-slate-800 hover:bg-slate-800/70'
            }`}
          >
            {labels[opt] || opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export function Chip({ text, onRemove }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900/60 border border-slate-800 text-slate-200 text-sm">
      <span className="max-w-[11rem] truncate">{text}</span>
      <button type="button" onClick={onRemove} className="text-xs text-slate-400 hover:text-red-400">✕</button>
    </div>
  );
}

/* ------------------ Icons (inline SVG) ------------------ */

function IconBase({ children, className }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
      {children}
    </svg>
  );
}

function IconBinoculars({ className }) {
  return (
    <IconBase className={className}>
      <circle cx="8" cy="13" r="3" />
      <circle cx="16" cy="13" r="3" />
      <path d="M5 13V9l2-3h3l1 2h2l1-2h3l2 3v4" />
    </IconBase>
  );
}

function IconKeyhole({ className }) {
  return (
    <IconBase className={className}>
      <path d="M12 3a5 5 0 0 0-2 9v3l2 2 2-2v-3a5 5 0 0 0-2-9z" />
    </IconBase>
  );
}

function IconTerminal({ className }) {
  return (
    <IconBase className={className}>
      <path d="M4 5h16v14H4z" />
      <path d="m7 9 3 3-3 3" />
      <path d="M13 15h4" />
    </IconBase>
  );
}

function IconPuzzle({ className }) {
  return (
    <IconBase className={className}>
      <path d="M10 5a2 2 0 1 1 4 0h3v4h-2a2 2 0 1 0 0 4h2v4h-4v-2a2 2 0 1 0-4 0v2H5v-4h2a2 2 0 1 0 0-4H5V5z" />
    </IconBase>
  );
}

function IconNetwork({ className }) {
  return (
    <IconBase className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 5v4M12 15v4M5 12h4M15 12h4M6.5 6.5l2.5 2.5M14.9 14.9l2.6 2.6M6.5 17.5l2.5-2.5M14.9 9.1l2.6-2.6" />
    </IconBase>
  );
}

function IconDatabase({ className }) {
  return (
    <IconBase className={className}>
      <ellipse cx="12" cy="6" rx="6" ry="3" />
      <path d="M6 6v6c0 1.7 2.7 3 6 3s6-1.3 6-3V6" />
    </IconBase>
  );
}

function IconZap({ className }) {
  return (
    <IconBase className={className}>
      <path d="M13 3 4 14h7l-1 7 9-11h-7z" />
    </IconBase>
  );
}

function IconShield({ className }) {
  return (
    <IconBase className={className}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </IconBase>
  );
}

/* ------------------ MITRE ATT&CK Tactics Grid ------------------ */

export const MITRE_DATA = [
  { key: 'recon', name: 'Reconnaissance', icon: IconBinoculars },
  { key: 'resource', name: 'Resource Development', icon: IconPuzzle },
  { key: 'initial', name: 'Initial Access', icon: IconKeyhole },
  { key: 'exec', name: 'Execution', icon: IconTerminal },
  { key: 'persist', name: 'Persistence', icon: IconPuzzle },
  { key: 'privilege', name: 'Privilege Escalation', icon: IconZap },
  { key: 'defense', name: 'Defense Evasion', icon: IconShield },
  { key: 'credential', name: 'Credential Access', icon: IconKeyhole },
  { key: 'discovery', name: 'Discovery', icon: IconBinoculars },
  { key: 'lateral', name: 'Lateral Movement', icon: IconNetwork },
  { key: 'collection', name: 'Collection', icon: IconDatabase },
  { key: 'command', name: 'Command and Control', icon: IconTerminal },
  { key: 'exfil', name: 'Exfiltration', icon: IconDatabase },
  { key: 'impact', name: 'Impact', icon: IconZap },
];

export function MITREGrid({ selectedDepth, onDepthChange }) {
  const handleTacticClick = (index) => {
    onDepthChange(index + 1);
  };

  return (
    <div className="mt-3">
      <div className="flex gap-3 overflow-x-auto pb-2">
        {MITRE_DATA.map((tactic, idx) => {
          const isSelected = idx < selectedDepth;
          const Icon = tactic.icon;
          return (
            <div key={tactic.key} className="flex-shrink-0">
              <div
                onClick={() => handleTacticClick(idx)}
                className={`w-32 h-24 rounded-lg border p-3 cursor-pointer transition ${
                  isSelected ? 'border-indigo-600/60 bg-indigo-600/10' : 'border-slate-800 bg-slate-900/40'
                } hover:border-indigo-500`}
              >
                <div className="flex flex-col items-center justify-center h-full gap-2">
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-indigo-400' : 'text-slate-400'}`} />
                  <div className="text-xs font-medium text-white text-center leading-tight">{tactic.name}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Selected depth: {selectedDepth} tactics. (
        {selectedDepth > 1
          ? `${MITRE_DATA[0].name} → ${MITRE_DATA[selectedDepth - 1].name}`
          : MITRE_DATA[0].name}
        )
      </div>
    </div>
  );
}

/* ------------------ Day / Hour selector ------------------
 * Calendar-grid day selector + hour picker. Used for defining testing windows
 * on offensive engagements. Pass `allowPast` to permit back-dated days — useful
 * when registering engagements that have already been executed.
 * ----------------------------------------------------------- */

export function DayHourSelector({ value = {}, onChange, allowPast = false }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [timeWindows, setTimeWindows] = useState(value || {});

  const [isDragging, setIsDragging] = useState(false);
  const [rangeSelectionStart, setRangeSelectionStart] = useState(null);

  const [isHourDragging, setIsHourDragging] = useState(false);
  const [hourDragStart, setHourDragStart] = useState(null);
  const [hourRangeStart, setHourRangeStart] = useState(null);

  // Keep internal state in sync when caller resets the value
  useEffect(() => {
    setTimeWindows(value || {});
  }, [value]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);

    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      // If allowPast is true, include all days. Otherwise only today and future.
      if (allowPast || dayDate >= today) {
        days.push({
          date: dayDate,
          key: dayDate.toISOString().split('T')[0],
          day: i,
          isPast: dayDate < today,
        });
      } else {
        days.push(null);
      }
    }
    return days;
  };

  const days = getDaysInMonth(currentMonth);

  const goToPreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() - 1);
    if (allowPast) {
      setCurrentMonth(newDate);
    } else {
      const today = new Date();
      if (newDate >= new Date(today.getFullYear(), today.getMonth(), 1)) {
        setCurrentMonth(newDate);
      }
    }
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentMonth(newDate);
  };

  const handleDayClick = (day, e) => {
    if (!day) return;
    e.preventDefault();

    if (e.shiftKey && rangeSelectionStart) {
      selectDayRange(rangeSelectionStart, day.key);
      return;
    }

    if (timeWindows[day.key]) {
      const updated = { ...timeWindows };
      delete updated[day.key];
      setTimeWindows(updated);
      onChange && onChange(updated);
      if (selectedDate === day.key) setSelectedDate(null);
      setRangeSelectionStart(null);
    } else {
      const updated = {
        ...timeWindows,
        [day.key]: Array.from({ length: 24 }, (_, i) => i),
      };
      setTimeWindows(updated);
      onChange && onChange(updated);
      setSelectedDate(day.key);
      setRangeSelectionStart(day.key);
    }
  };

  const handleDayMouseDown = (day, e) => {
    if (!day || e.button !== 0 || e.shiftKey) return;
    setIsDragging(true);
  };

  const handleDayMouseEnter = (day) => {
    if (!day || !isDragging) return;
    if (!timeWindows[day.key]) {
      const updated = {
        ...timeWindows,
        [day.key]: Array.from({ length: 24 }, (_, i) => i),
      };
      setTimeWindows(updated);
      onChange && onChange(updated);
    }
  };

  const handleDayMouseUp = () => setIsDragging(false);

  const selectDayRange = (startKey, endKey) => {
    const allDays = days.filter((d) => d !== null);
    const startIndex = allDays.findIndex((d) => d.key === startKey);
    const endIndex = allDays.findIndex((d) => d.key === endKey);
    if (startIndex === -1 || endIndex === -1) return;

    const [from, to] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
    const updated = { ...timeWindows };
    for (let i = from; i <= to; i++) {
      const dayKey = allDays[i].key;
      if (!updated[dayKey]) {
        updated[dayKey] = Array.from({ length: 24 }, (_, i) => i);
      }
    }
    setTimeWindows(updated);
    onChange && onChange(updated);
    setSelectedDate(endKey);
  };

  const applyHoursPreset = (preset) => {
    if (!selectedDate) return;
    let hours = [];
    if (preset === '24h') hours = Array.from({ length: 24 }, (_, i) => i);
    else if (preset === 'work') hours = Array.from({ length: 10 }, (_, i) => i + 8);
    const updated = { ...timeWindows, [selectedDate]: hours };
    setTimeWindows(updated);
    onChange && onChange(updated);
  };

  const toggleHour = (hour) => {
    if (!selectedDate) return;
    const currentHours = timeWindows[selectedDate] || [];
    const updated = {
      ...timeWindows,
      [selectedDate]: currentHours.includes(hour)
        ? currentHours.filter((h) => h !== hour)
        : [...currentHours, hour].sort((a, b) => a - b),
    };
    setTimeWindows(updated);
    onChange && onChange(updated);
  };

  const handleHourClick = (hour, e) => {
    if (!selectedDate) return;
    e.preventDefault();
    if (e.shiftKey && hourRangeStart !== null) {
      selectHourRange(hourRangeStart, hour);
      return;
    }
    toggleHour(hour);
    setHourRangeStart(hour);
  };

  const handleHourMouseDown = (hour, e) => {
    if (e.button !== 0 || e.shiftKey) return;
    setIsHourDragging(true);
    const currentHours = timeWindows[selectedDate] || [];
    const shouldAdd = !currentHours.includes(hour);
    setHourDragStart({ hour, shouldAdd });
  };

  const handleHourMouseEnter = (hour) => {
    if (!isHourDragging || !hourDragStart || !selectedDate) return;
    const currentHours = timeWindows[selectedDate] || [];
    const isSelected = currentHours.includes(hour);
    if (hourDragStart.shouldAdd && !isSelected) {
      const updated = {
        ...timeWindows,
        [selectedDate]: [...currentHours, hour].sort((a, b) => a - b),
      };
      setTimeWindows(updated);
      onChange && onChange(updated);
    } else if (!hourDragStart.shouldAdd && isSelected) {
      const updated = {
        ...timeWindows,
        [selectedDate]: currentHours.filter((h) => h !== hour),
      };
      setTimeWindows(updated);
      onChange && onChange(updated);
    }
  };

  const handleHourMouseUp = () => {
    setIsHourDragging(false);
    setHourDragStart(null);
  };

  const selectHourRange = (startHour, endHour) => {
    if (!selectedDate) return;
    const [from, to] = startHour <= endHour ? [startHour, endHour] : [endHour, startHour];
    const currentHours = timeWindows[selectedDate] || [];
    const newHours = Array.from({ length: to - from + 1 }, (_, i) => from + i);
    const updated = {
      ...timeWindows,
      [selectedDate]: [...new Set([...currentHours, ...newHours])].sort((a, b) => a - b),
    };
    setTimeWindows(updated);
    onChange && onChange(updated);
  };

  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${i.toString().padStart(2, '0')}:00`,
  }));

  const monthYear = currentMonth.toLocaleDateString('en', { month: 'long', year: 'numeric' });
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isCurrentMonth =
    currentMonth.getMonth() === today.getMonth() &&
    currentMonth.getFullYear() === today.getFullYear();

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      handleDayMouseUp();
      handleHourMouseUp();
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      <div className="flex-shrink-0" style={{ width: '280px' }}>
        <div className="flex items-center justify-between mb-2">
          <button
            type="button"
            onClick={goToPreviousMonth}
            disabled={!allowPast && isCurrentMonth}
            className={`p-1 rounded ${
              !allowPast && isCurrentMonth
                ? 'text-slate-600 cursor-not-allowed'
                : 'text-slate-300 hover:bg-slate-800'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-xs font-medium text-slate-300 capitalize">{monthYear}</h3>
          <button
            type="button"
            onClick={goToNextMonth}
            className="p-1 rounded text-slate-300 hover:bg-slate-800"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-0.5">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dayName, i) => (
            <div key={i} className="text-center text-[10px] font-medium text-slate-500 py-1">
              {dayName}
            </div>
          ))}
          {days.map((day, index) => {
            if (!day) return <div key={`empty-${index}`} className="w-9 h-9" />;

            const isSelected = timeWindows[day.key] !== undefined;
            const isActiveDay = selectedDate === day.key;

            return (
              <button
                key={day.key}
                type="button"
                onClick={(e) => handleDayClick(day, e)}
                onMouseDown={(e) => handleDayMouseDown(day, e)}
                onMouseEnter={() => handleDayMouseEnter(day)}
                className={`w-9 h-9 rounded text-xs transition select-none ${
                  isActiveDay
                    ? 'bg-indigo-600 text-white font-semibold ring-1 ring-indigo-400'
                    : isSelected
                    ? day.isPast
                      ? 'bg-amber-500/60 text-white font-medium'
                      : 'bg-indigo-500/60 text-white font-medium'
                    : day.isPast
                    ? 'bg-slate-900/30 text-slate-600 hover:bg-slate-800 hover:text-slate-400'
                    : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
                }`}
              >
                {day.day}
              </button>
            );
          })}
        </div>

        {Object.keys(timeWindows).length > 0 && (
          <div className="mt-3 p-2 rounded bg-slate-900/50 border border-slate-800">
            <div className="text-[10px] font-medium text-slate-400 mb-1">SELECTED</div>
            <div className="text-xs text-slate-300">
              {Object.keys(timeWindows).length} days · {Object.values(timeWindows).flat().length} hours
            </div>
          </div>
        )}

        <div className="mt-3 p-2 rounded bg-slate-950/30 border border-slate-800/50">
          <div className="text-[10px] text-slate-500 space-y-1">
            <div>🖱️ <span className="text-slate-400">Click</span> to select/deselect</div>
            <div>↔️ <span className="text-slate-400">Drag</span> multiple days</div>
            <div>⇧ <span className="text-slate-400">Shift + Click</span> for range</div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {selectedDate ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-800">
              <div>
                <h4 className="text-sm font-medium text-slate-300">
                  {new Date(selectedDate).toLocaleDateString('en', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                  })}
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  {(timeWindows[selectedDate] || []).length} hours selected
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => applyHoursPreset('work')}
                  className="px-2 py-1 text-xs rounded bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  Work
                </button>
                <button
                  type="button"
                  onClick={() => applyHoursPreset('24h')}
                  className="px-2 py-1 text-xs rounded bg-slate-800 text-slate-300 hover:bg-slate-700"
                >
                  24h
                </button>
              </div>
            </div>

            <div className="grid grid-cols-6 gap-1 max-h-64 overflow-y-auto pr-2">
              {hours.map((hour) => {
                const isSelected = (timeWindows[selectedDate] || []).includes(hour.value);
                return (
                  <button
                    key={hour.value}
                    type="button"
                    onClick={(e) => handleHourClick(hour.value, e)}
                    onMouseDown={(e) => handleHourMouseDown(hour.value, e)}
                    onMouseEnter={() => handleHourMouseEnter(hour.value)}
                    className={`px-3 py-1.5 rounded text-xs transition select-none ${
                      isSelected
                        ? 'bg-indigo-500 text-white font-medium'
                        : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700 hover:text-slate-300'
                    }`}
                  >
                    {hour.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-center py-12">
            <div>
              <svg className="w-12 h-12 mx-auto text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-slate-500">Select a day from the calendar</p>
              <p className="text-xs text-slate-600 mt-1">to configure the allowed window</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
